import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Activity, Shield, Wifi, TrendingUp, Clock } from 'lucide-react';

const DashboardPage = () => {
  const [uptime] = useState('99.8%');
  const [threats, setThreats] = useState(6088);
  const [scans, setScans] = useState(15432);

  // Simulate live updates (replace with real API calls)
  useEffect(() => {
    const interval = setInterval(() => {
      setThreats(prev => prev + Math.floor(Math.random() * 3));
      setScans(prev => prev + 1);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const stats = [
    { label: 'Active Agents', value: '4', icon: Activity, color: 'text-purple-400' },
    { label: 'System Uptime', value: uptime, icon: Clock, color: 'text-green-400' },
    { label: 'Threats Blocked', value: threats.toLocaleString(), icon: Shield, color: 'text-red-400' },
    { label: 'Network Scans', value: scans.toLocaleString(), icon: Wifi, color: 'text-blue-400' }
  ];

  const agentStatus = [
    { name: 'Guardian', status: 'operational', lastCheck: '2 seconds ago', color: 'bg-green-500' },
    { name: 'Caretaker', status: 'operational', lastCheck: '5 seconds ago', color: 'bg-orange-500' },
    { name: 'Network Agent', status: 'operational', lastCheck: '1 second ago', color: 'bg-blue-500' },
    { name: 'Herald', status: 'operational', lastCheck: '3 seconds ago', color: 'bg-purple-500' }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl md:text-6xl font-bold text-green-400 mb-4">
            Live System Dashboard
          </h1>
          <p className="text-xl text-gray-300">
            Real-time anonymized stats from a production MacMind deployment
          </p>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
        >
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="bg-black/50 border border-green-500/20 rounded-lg p-6 hover:border-green-500/40 transition-colors"
            >
              <div className="flex items-center justify-between mb-3">
                <stat.icon className={`w-10 h-10 ${stat.color}`} />
                <TrendingUp className="w-6 h-6 text-green-400" />
              </div>
              <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
              <div className="text-sm text-gray-400">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Agent Status */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mb-12"
        >
          <h2 className="text-3xl font-bold text-green-400 mb-6">Agent Health</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {agentStatus.map((agent) => (
              <div
                key={agent.name}
                className="bg-black/50 border border-green-500/20 rounded-lg p-6 flex items-center justify-between"
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-3 h-3 rounded-full ${agent.color} animate-pulse`} />
                  <div>
                    <div className="text-xl font-bold text-white">{agent.name}</div>
                    <div className="text-sm text-gray-400">{agent.lastCheck}</div>
                  </div>
                </div>
                <div className="text-green-400 font-semibold uppercase text-sm">
                  {agent.status}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Live Feed */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="bg-black/50 border border-green-500/20 rounded-lg p-6"
        >
          <h2 className="text-3xl font-bold text-green-400 mb-6">Recent Activity</h2>
          <div className="space-y-3 font-mono text-sm">
            <div className="text-gray-300">
              <span className="text-green-400">[{new Date().toLocaleTimeString()}]</span> Guardian: Blocked malicious domain
            </div>
            <div className="text-gray-300">
              <span className="text-green-400">[{new Date(Date.now() - 15000).toLocaleTimeString()}]</span> Network Agent: Completed network topology scan
            </div>
            <div className="text-gray-300">
              <span className="text-green-400">[{new Date(Date.now() - 30000).toLocaleTimeString()}]</span> Caretaker: System health check passed
            </div>
            <div className="text-gray-300">
              <span className="text-green-400">[{new Date(Date.now() - 45000).toLocaleTimeString()}]</span> Herald: Notification delivered to Discord
            </div>
          </div>
        </motion.div>

        {/* Privacy Notice */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-8 text-center text-gray-500 text-sm"
        >
          <p>All data shown is anonymized. No device names, IPs, or personal information is displayed.</p>
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardPage;
